// frontend/src/pages/EnhancedHistoricalLoanImport.jsx
// FIXED: Top-ups now use /backdate-topup endpoint which properly consolidates with existing loans

import React, { useEffect, useState, useRef } from 'react';

// ── Searchable member picker ──────────────────────────────────────────────────
function MemberSearch({ members, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = members.find(m => m.memberId === value);
  const displayLabel = selected
    ? `${selected.memberId} - ${selected.personalInfo?.fullName || ''}`
    : '';

  const inputValue = open ? query : displayLabel;

  const filtered = query.trim()
    ? members.filter(m => {
        const q = query.toLowerCase();
        return (
          m.memberId?.toLowerCase().includes(q) ||
          (m.personalInfo?.fullName || '').toLowerCase().includes(q)
        );
      })
    : members;

  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(m) {
    onChange(m.memberId);
    setQuery('');
    setOpen(false);
  }

  function handleInputChange(e) {
    setQuery(e.target.value);
    setOpen(true);
    if (!e.target.value) onChange('');
  }

  function handleFocus() {
    setQuery('');
    setOpen(true);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder="Type ID or name to search..."
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        autoComplete="off"
        style={{
          width: '100%',
          padding: '6px 10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          fontSize: '14px',
          boxSizing: 'border-box'
        }}
      />
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'white',
          border: '1px solid #ccc',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          maxHeight: '260px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '10px', color: '#888', fontSize: '13px' }}>No members found</div>
          ) : (
            filtered.map(m => (
              <div
                key={m.id || m.memberId}
                onMouseDown={() => handleSelect(m)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  background: m.memberId === value ? '#ede9fe' : 'white',
                  fontWeight: m.memberId === value ? 600 : 400,
                  borderBottom: '1px solid #f0f0f0'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f3f0ff'}
                onMouseLeave={e => e.currentTarget.style.background = m.memberId === value ? '#ede9fe' : 'white'}
              >
                <span style={{ color: '#4b3ff6', fontWeight: 600 }}>{m.memberId}</span>
                {' - '}
                {m.personalInfo?.fullName || ''}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import * as XLSX from 'xlsx';
import { writeActivityLog } from '../utils/activityLog';
import globalApi from '../utils/globalFetchWrapper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const defaultFirst = {
  memberId: '',
  loanType: 'normal',
  originalAmount: '',
  disbursementDate: '',
  duration: '',
  monthlyPrincipalPayment: '',
  applicationFee: ''
};

const defaultTopupRow = {
  loanType: 'normal',
  originalAmount: '',
  disbursementDate: '',
  duration: '',
  monthlyPrincipalPayment: '',
  applicationFee: ''
};

export default function EnhancedHistoricalLoanImport() {
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState('first');

  const [first, setFirst] = useState({ ...defaultFirst });

  const [topMemberId, setTopMemberId] = useState('');
  const [topups, setTopups] = useState([{ ...defaultTopupRow }]);

  const [excelRows, setExcelRows] = useState([]);
  const [excelFile, setExcelFile] = useState(null);

  const [busy, setBusy] = useState(false);
  // Backwards-compat: some versions used `loading`/`setLoading`
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  useEffect(() => { loadMembers(); }, []);

  async function loadMembers() {
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/reports/members-for-statement`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const j = await r.json();
      if (j.success) setMembers(j.data?.members || []);
    } catch (e) { console.error(e); }
  }

  const memberOptions = (
    <>
      <option value="">-- Select --</option>
      {members.map(m => (
        <option key={m.id} value={m.memberId}>
          {m.memberId} - {m.personalInfo?.fullName || ''}
        </option>
      ))}
    </>
  );

  const validNum = (n) => n !== '' && !isNaN(Number(n));

  async function submitFirstLoan(e) {
    e.preventDefault();
    setBusy(true); setError(''); setOk('');
    try {
      const token = localStorage.getItem('hopeSaccoToken');

      const payload = {
        memberId: String(first.memberId || '').trim(),
        loanType: (first.loanType || 'normal').toLowerCase(),
        originalAmount: Number(first.originalAmount),
        disbursementDate: first.disbursementDate,
        duration: Number(first.duration),
        monthlyPrincipalPayment: validNum(first.monthlyPrincipalPayment) ? Number(first.monthlyPrincipalPayment) : undefined,
        applicationFee: validNum(first.applicationFee) ? Number(first.applicationFee) : undefined
      };

      if (!payload.memberId) throw new Error('Member is required');
      if (!payload.originalAmount) throw new Error('Amount is required');
      if (!payload.disbursementDate) throw new Error('Disbursement Date is required');
      if (!payload.duration) throw new Error('Duration (months) is required');
      if (!validNum(first.applicationFee)) throw new Error('Application Fee is required');

      const r = await fetch(`${API_URL}/loans/backdate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      const j = await r.json();
      if (!r.ok || !j.success) throw new Error(j.message || 'Backdate failed');

      setOk(`First ${first.loanType} loan saved successfully. Monthly payment: KES ${j.data?.monthlyPayment?.toLocaleString() || 'N/A'}`);
      writeActivityLog({
        source: 'historical_loan_first',
        memberId: first.memberId,
        memberName: members.find(m => m.memberId === first.memberId)?.personalInfo?.fullName || first.memberId,
        loanType: first.loanType,
        amount: Number(first.originalAmount),
        disbursementDate: first.disbursementDate,
        duration: Number(first.duration),
        applicationFee: first.applicationFee ? Number(first.applicationFee) : undefined,
        monthlyPayment: j.data?.monthlyPayment,      });
      setFirst({ ...defaultFirst });
    } catch (e) { setError(e.message || 'Failed to save first loan'); }
    finally { setBusy(false); }
  }

  async function submitTopups(e) {
    e.preventDefault();
    setBusy(true); setError(''); setOk('');
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const memberId = String(topMemberId || '').trim();
      if (!memberId) throw new Error('Member is required for top-up(s)');

      const rows = topups.filter(x =>
        x.loanType && x.originalAmount && x.disbursementDate && x.duration
      );
      if (!rows.length) throw new Error('Add at least one valid top-up row');

      const results = [];

      for (const x of rows) {
        const body = {
          memberId,
          loanType: (x.loanType || 'normal').toLowerCase(),
          originalAmount: Number(x.originalAmount),
          disbursementDate: x.disbursementDate,
          duration: Number(x.duration),
          monthlyPrincipalPayment: validNum(x.monthlyPrincipalPayment) ? Number(x.monthlyPrincipalPayment) : undefined,
          applicationFee: validNum(x.applicationFee) ? Number(x.applicationFee) : undefined
        };

        if (!body.originalAmount) throw new Error('Top-up amount must be a number');
        if (!body.duration) throw new Error('Top-up duration (months) is required');
        if (!validNum(x.applicationFee)) throw new Error('Application Fee is required for each top-up row');

        const r = await fetch(`${API_URL}/loans/backdate-topup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify(body)
        });
        const j = await r.json();
        if (!r.ok || !j.success) {
          throw new Error(j.message || `Top-up failed for ${body.loanType} ${body.originalAmount}`);
        }
        results.push(j.data);
      }

      const consolidatedInfo = results.length > 0 && results[results.length - 1].isConsolidated
        ? `Consolidated monthly payment: KES ${results[results.length - 1].monthlyPayment?.toLocaleString()}`
        : '';

      setOk(`Imported ${rows.length} top-up(s) successfully. ${consolidatedInfo}`);
      rows.forEach(x => {
        writeActivityLog({
          source: 'historical_loan_topup',
          memberId: topMemberId,
          memberName: members.find(m => m.memberId === topMemberId)?.personalInfo?.fullName || topMemberId,
          loanType: x.loanType,
          amount: Number(x.originalAmount),
          disbursementDate: x.disbursementDate,
          duration: Number(x.duration),
          applicationFee: x.applicationFee ? Number(x.applicationFee) : undefined,        });
      });
      setTopups([{ ...defaultTopupRow }]);
    } catch (e) { setError(e.message || 'Failed to import top-up(s)'); }
    finally { setBusy(false); }
  }

  function handleExcel(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setExcelFile(file);
    setLoading(true);
    const reader = new FileReader();
    reader.onerror = () => {
      setError('Failed to read the selected Excel file');
      setLoading(false);
    };
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'binary' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      setExcelRows(rows);
      setLoading(false);
    };
    reader.readAsBinaryString(file);
  }

  // Normalize Excel row data to handle column names with guidance text
  function normalizeExcelRow(row) {
    const normalized = {};
    
    // Map column names (with or without guidance) to standard field names
    const fieldMappings = {
      'memberId': ['memberId'],
      'loanType': ['loanType', 'loanType (normal/school/emergency)'],
      'originalAmount': ['originalAmount'],
      'disbursementDate': ['disbursementDate', 'disbursementDate (any date format)', 'disbursementDate (YYYY-MM-DD)'],
      'duration': ['duration', 'duration (months)'],
      'monthlyPrincipalPayment': ['monthlyPrincipalPayment', 'monthlyPrincipalPayment (optional)'],
      'applicationFee': ['applicationFee', 'applicationFee (optional)'],
      'initialShares': ['initialShares', 'initialShares (optional)'],
      'initialSharesDate': ['initialSharesDate', 'initialSharesDate (optional)'],
      'sharesStartMonth': ['sharesStartMonth', 'sharesStartMonth (optional, YYYY-MM)', 'sharesStartMonth (optional)'],
      'monthlySharesContribution': ['monthlySharesContribution', 'monthlySharesContribution (optional)']
    };
    
    // For each standard field, find the matching column in the row
    for (const [standardField, possibleNames] of Object.entries(fieldMappings)) {
      for (const possibleName of possibleNames) {
        if (row.hasOwnProperty(possibleName) && row[possibleName] !== undefined && row[possibleName] !== '') {
          normalized[standardField] = row[possibleName];
          break;
        }
      }
    }
    
    return normalized;
  }

  // Validate Excel data before uploading
  function validateExcelData(rows) {
    const errors = [];
    const validLoanTypes = ['normal', 'school', 'emergency'];
    
    rows.forEach((row, index) => {
      const rowNum = index + 2; // +2 because Excel rows start at 1 and there's a header
      const normalizedRow = normalizeExcelRow(row);
      
      // Required fields validation
      if (!normalizedRow.memberId || String(normalizedRow.memberId).trim() === '') {
        errors.push(`Row ${rowNum}: memberId is required`);
      }
      
      // Helper to check if a value represents a real positive number (handling floating point precision)
      const isRealPositiveNumber = (val) => {
        if (val === null || val === undefined || val === '') return false;
        const num = Number(val);
        if (isNaN(num)) return false;
        // Treat very small numbers (< 0.01) as zero to handle floating point precision issues
        // Also handle Excel formula errors like -5.82e-11
        return num >= 0.01;
      };
      
      // Check if row has loan data
      const hasLoanAmount = isRealPositiveNumber(normalizedRow.originalAmount);
      const hasDuration = normalizedRow.duration && Number(normalizedRow.duration) > 0;
      
      // Check if row has shares data
      const hasInitialShares = isRealPositiveNumber(normalizedRow.initialShares);
      const hasMonthlyShares = isRealPositiveNumber(normalizedRow.monthlySharesContribution);
      const hasSharesData = hasInitialShares || hasMonthlyShares;
      
      // Skip rows with all zeros - they're empty/placeholder rows
      // Don't require loan OR shares - if both are zero, we'll just skip this row during import
      
      // Only validate loanType if there's a real loan amount
      if (hasLoanAmount) {
        if (!normalizedRow.loanType || String(normalizedRow.loanType).trim() === '') {
          errors.push(`Row ${rowNum}: loanType is required when originalAmount > 0`);
        } else {
          const loanType = String(normalizedRow.loanType).toLowerCase().trim();
          if (!validLoanTypes.includes(loanType)) {
            errors.push(`Row ${rowNum}: loanType must be 'normal', 'school', or 'emergency' (got '${normalizedRow.loanType}')`);
          }
        }
        
        if (isNaN(Number(normalizedRow.originalAmount))) {
          errors.push(`Row ${rowNum}: originalAmount must be a valid number (got '${normalizedRow.originalAmount}')`);
        }
        
        if (!normalizedRow.disbursementDate && normalizedRow.disbursementDate !== 0) {
          errors.push(`Row ${rowNum}: disbursementDate is required when originalAmount > 0`);
        } else {
          // Try to parse the date
          let dateValid = false;
          
          // Check if it's already a Date object
          if (normalizedRow.disbursementDate instanceof Date && !isNaN(normalizedRow.disbursementDate.getTime())) {
            dateValid = true;
          } 
          // Check if it's an Excel serial number (positive number)
          else if (typeof normalizedRow.disbursementDate === 'number' && normalizedRow.disbursementDate > 0) {
            dateValid = true;
          }
          // Check if it's a string that can be parsed
          else if (typeof normalizedRow.disbursementDate === 'string' && normalizedRow.disbursementDate.trim() !== '') {
            const testDate = new Date(normalizedRow.disbursementDate);
            if (!isNaN(testDate.getTime())) {
              dateValid = true;
            }
          }
          
          if (!dateValid) {
            errors.push(`Row ${rowNum}: disbursementDate is not a valid date (got '${normalizedRow.disbursementDate}')`);
          }
        }
        
        if (!hasDuration) {
          errors.push(`Row ${rowNum}: duration is required when originalAmount > 0`);
        } else if (isNaN(Number(normalizedRow.duration))) {
          errors.push(`Row ${rowNum}: duration must be a valid number (got '${normalizedRow.duration}')`);
        }
      }
      
      // Optional fields validation (only if provided)
      if (normalizedRow.monthlyPrincipalPayment && normalizedRow.monthlyPrincipalPayment !== '') {
        if (isNaN(Number(normalizedRow.monthlyPrincipalPayment)) || Number(normalizedRow.monthlyPrincipalPayment) <= 0) {
          errors.push(`Row ${rowNum}: monthlyPrincipalPayment must be a positive number if provided (got '${normalizedRow.monthlyPrincipalPayment}')`);
        }
      }
      
      if (normalizedRow.applicationFee && normalizedRow.applicationFee !== '') {
        if (isNaN(Number(normalizedRow.applicationFee)) || Number(normalizedRow.applicationFee) < 0) {
          errors.push(`Row ${rowNum}: applicationFee must be a non-negative number if provided (got '${normalizedRow.applicationFee}')`);
        }
      }
      
      if (normalizedRow.initialShares && normalizedRow.initialShares !== '') {
        if (isNaN(Number(normalizedRow.initialShares)) || Number(normalizedRow.initialShares) < 0) {
          errors.push(`Row ${rowNum}: initialShares must be a non-negative number if provided (got '${normalizedRow.initialShares}')`);
        }
      }
      
      if (normalizedRow.initialSharesDate && normalizedRow.initialSharesDate !== '') {
        let dateValid = false;
        
        if (normalizedRow.initialSharesDate instanceof Date && !isNaN(normalizedRow.initialSharesDate.getTime())) {
          dateValid = true;
        } else if (typeof normalizedRow.initialSharesDate === 'number' && normalizedRow.initialSharesDate > 0) {
          dateValid = true;
        } else if (typeof normalizedRow.initialSharesDate === 'string' && normalizedRow.initialSharesDate.trim() !== '') {
          const testDate = new Date(normalizedRow.initialSharesDate);
          if (!isNaN(testDate.getTime())) {
            dateValid = true;
          }
        }
        
        if (!dateValid) {
          errors.push(`Row ${rowNum}: initialSharesDate is not a valid date if provided (got '${normalizedRow.initialSharesDate}')`);
        }
      }
      
      if (normalizedRow.sharesStartMonth && normalizedRow.sharesStartMonth !== '') {
        const startMonthStr = String(normalizedRow.sharesStartMonth).trim();
        // Validate YYYY-MM format
        if (!/^\d{4}-\d{2}$/.test(startMonthStr)) {
          errors.push(`Row ${rowNum}: sharesStartMonth must be in YYYY-MM format (got '${normalizedRow.sharesStartMonth}')`);
        }
      }
      
      if (normalizedRow.monthlySharesContribution && normalizedRow.monthlySharesContribution !== '') {
        if (isNaN(Number(normalizedRow.monthlySharesContribution)) || Number(normalizedRow.monthlySharesContribution) < 0) {
          errors.push(`Row ${rowNum}: monthlySharesContribution must be a non-negative number if provided (got '${normalizedRow.monthlySharesContribution}')`);
        }
      }
    });
    
    return errors;
  }

  async function submitExcel() {
    setBusy(true); setError(''); setOk('');
    try {
      // Validate data first
      const validationErrors = validateExcelData(excelRows);
      if (validationErrors.length > 0) {
        setError(`Found ${validationErrors.length} validation error(s):\n\n${validationErrors.join('\n')}`);
        setBusy(false);
        return;
      }

      const token = localStorage.getItem('hopeSaccoToken');
      const memberLoans = {};

      for (const row of excelRows) {
        const normalizedRow = normalizeExcelRow(row);
        const memberId = String(normalizedRow.memberId || '').trim();
        if (!memberId) continue;

        if (!memberLoans[memberId]) {
          memberLoans[memberId] = [];
        }
        memberLoans[memberId].push(normalizedRow);
      }

      let totalProcessed = 0;
      const errors = [];
      const allRequests = []; // Collect all requests for batch processing

      for (const [memberId, loans] of Object.entries(memberLoans)) {
        loans.sort((a, b) => {
          const dateA = a.disbursementDate instanceof Date ? a.disbursementDate : new Date(a.disbursementDate);
          const dateB = b.disbursementDate instanceof Date ? b.disbursementDate : new Date(b.disbursementDate);
          return dateA - dateB;
        });

        for (let i = 0; i < loans.length; i++) {
          const loan = loans[i];
          const isFirst = i === 0;
          
          // Helper to check if value is a real positive number
          const isRealPositiveNumber = (val) => {
            if (val === null || val === undefined || val === '') return false;
            const num = Number(val);
            if (isNaN(num)) return false;
            // Only accept positive numbers >= 0.01 (not absolute value)
            return num >= 0.01;
          };
          
          // Check if this row has a real loan amount
          const hasRealLoan = isRealPositiveNumber(loan.originalAmount);
          
          // Check if row has shares data
          const hasInitialShares = validNum(loan.initialShares) && Number(loan.initialShares) >= 0.01;
          const hasMonthlyShares = validNum(loan.monthlySharesContribution) && Number(loan.monthlySharesContribution) >= 0.01;
          const hasSharesData = hasInitialShares || hasMonthlyShares;
          
          // Skip rows with all zeros (no loan and no shares)
          if (!hasRealLoan && !hasSharesData) {
            console.log(`Skipping row with memberId ${memberId} - all zeros (no loan, no shares)`);
            continue;
          }
          
          // Helper to convert Excel serial date to YYYY-MM-DD
          const convertExcelDate = (dateValue) => {
            if (!dateValue && dateValue !== 0) return null;
            
            // Already a Date object
            if (dateValue instanceof Date) {
              const year = dateValue.getFullYear();
              const month = String(dateValue.getMonth() + 1).padStart(2, '0');
              const day = String(dateValue.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
            
            // Excel serial number (days since 1900-01-01)
            if (typeof dateValue === 'number') {
              const excelEpoch = new Date(1900, 0, 1);
              const days = dateValue - 2; // Excel incorrectly treats 1900 as a leap year
              const date = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
            
            // Already a string - just trim
            if (typeof dateValue === 'string') {
              return dateValue.trim();
            }
            
            return null;
          };
          
          // Format disbursementDate properly
          const disbursementDate = convertExcelDate(loan.disbursementDate);

          const payload = {
            memberId
          };
          
          console.log(`Row for ${memberId}: hasRealLoan=${hasRealLoan}, hasSharesData=${hasSharesData}`);
          
          // Only add loan fields if there's a real loan amount
          if (hasRealLoan) {
            payload.loanType = (loan.loanType || 'normal').toLowerCase().trim();
            payload.originalAmount = Number(loan.originalAmount);
            payload.disbursementDate = disbursementDate;
            payload.duration = Number(loan.duration);
            payload.monthlyPrincipalPayment = validNum(loan.monthlyPrincipalPayment) ? Number(loan.monthlyPrincipalPayment) : undefined;
            payload.applicationFee = validNum(loan.applicationFee) ? Number(loan.applicationFee) : undefined;
          }
          
          console.log(`Payload for ${memberId}:`, JSON.stringify(payload, null, 2));
          
          // Add shares data from first row only
          if (isFirst) {
            console.log(`  Checking shares for ${memberId}:`, {
              initialShares: loan.initialShares,
              validNum: validNum(loan.initialShares),
              greaterThan001: Number(loan.initialShares) >= 0.01,
              monthlyShares: loan.monthlySharesContribution,
              sharesStartMonth: loan.sharesStartMonth
            });
            
            if (validNum(loan.initialShares) && Number(loan.initialShares) >= 0.01) {
              payload.initialShares = Number(loan.initialShares);
              console.log(`    Added initialShares: ${payload.initialShares}`);
            }
            if (loan.initialSharesDate) {
              // Use the same Excel date converter
              payload.initialSharesDate = convertExcelDate(loan.initialSharesDate);
              console.log(`    Added initialSharesDate: ${payload.initialSharesDate}`);
            }
            if (loan.sharesStartMonth && String(loan.sharesStartMonth).trim() !== '') {
              payload.sharesStartMonth = String(loan.sharesStartMonth).trim();
              console.log(`    Added sharesStartMonth: ${payload.sharesStartMonth}`);
            }
            if (validNum(loan.monthlySharesContribution) && Number(loan.monthlySharesContribution) >= 0.01) {
              payload.monthlySharesContribution = Number(loan.monthlySharesContribution);
              console.log(`    Added monthlySharesContribution: ${payload.monthlySharesContribution}`);
            }
          }

          const endpoint = isFirst ? '/loans/backdate' : '/loans/backdate-topup';

          // Collect request for batch processing instead of making immediate call
          allRequests.push({
            memberId,
            loanType: loan.loanType,
            payload,
            endpoint
          });
        }
      }

      // Process all requests in batches to avoid rate limiting
      const BATCH_SIZE = 10;
      const BATCH_DELAY = 1000; // 1 second delay between batches
      
      for (let i = 0; i < allRequests.length; i += BATCH_SIZE) {
        const batch = allRequests.slice(i, i + BATCH_SIZE);
        const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(allRequests.length / BATCH_SIZE);
        
        setOk(`Processing batch ${batchNumber}/${totalBatches} (${batch.length} records)...`);

        // Process current batch in parallel
        const batchPromises = batch.map(async (request) => {
          try {
            const r = await fetch(`${API_URL}${request.endpoint}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
              },
              body: JSON.stringify(request.payload)
            });
            const j = await r.json();
            if (!r.ok || !j.success) {
              errors.push(`${request.memberId} - ${request.loanType || 'shares'}: ${j.message || 'Failed'}`);
            } else {
              totalProcessed++;
            }
          } catch (err) {
            errors.push(`${request.memberId} - ${request.loanType || 'shares'}: ${err.message}`);
          }
        });

        await Promise.all(batchPromises);

        // Add delay before next batch (except for last batch)
        if (i + BATCH_SIZE < allRequests.length) {
          await new Promise(resolve => setTimeout(resolve, BATCH_DELAY));
        }
      }

      if (errors.length > 0) {
        setError(`Processed ${totalProcessed} records with ${errors.length} errors:\n${errors.join('\n')}`);
      } else {
        setOk(`✅ Successfully imported ${totalProcessed} records (loans and/or shares).`);
        allRequests.forEach(req => {
          writeActivityLog({
            source: 'historical_loan_excel',
            memberId: req.memberId,
            memberName: members.find(m => m.memberId === req.memberId)?.personalInfo?.fullName || req.memberId,
            loanType: req.loanType || 'shares',
            amount: req.payload.originalAmount || undefined,
            disbursementDate: req.payload.disbursementDate || undefined,
            duration: req.payload.duration || undefined,          });
        });
      }
      setExcelRows([]);
    } catch (e) { setError(e.message || 'Excel import failed'); }
    finally { setBusy(false); }
  }

  return (
    <AdminLayout activePage="historical-import" pageTitle="Import Historical Loans">
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="tab-buttons">
              <button className={tab === 'first' ? 'btn btn-primary' : 'btn btn-outline'} onClick={() => setTab('first')}>
                First Loan (Backdate)
              </button>
              <button className={tab === 'topup' ? 'btn btn-primary' : 'btn btn-outline'} onClick={() => setTab('topup')}>
                Top-Up (with Consolidation)
              </button>
              <button className={tab === 'excel' ? 'btn btn-secondary' : 'btn btn-outline'} onClick={() => setTab('excel')}>
                Excel Import
              </button>
            </div>
            <button
              type="button"
              onClick={() => navigate('/admin/activity-log')}
              style={{ padding: '8px 16px', fontSize: '14px', background: '#4b3ff6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            >
              📋 Log
            </button>
          </div>
        </div>

        <div className="card-body">
          {error && <div className="alert alert-error">{error}</div>}
          {ok && <div className="alert alert-success">{ok}</div>}

          {tab === 'first' && (
            <form onSubmit={submitFirstLoan}>
              <div className="info-box info-box-muted">
                <p>
                  <strong>First Loan / Initial Loan:</strong> Use this for a member's FIRST loan of a type. 
                  This creates an independent loan with its own payment schedule.
                </p>
              </div>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Member *</label>
                  <MemberSearch members={members} value={first.memberId} onChange={v => setFirst({ ...first, memberId: v })} />
                </div>
                <div className="form-group">
                  <label>Loan Type *</label>
                  <select value={first.loanType} onChange={e => setFirst({ ...first, loanType: e.target.value })}>
                    <option value="normal">Normal</option>
                    <option value="school">School</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount *</label>
                  <input type="number" value={first.originalAmount} onChange={e => setFirst({ ...first, originalAmount: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Disbursement Date *</label>
                  <input type="date" value={first.disbursementDate} onChange={e => setFirst({ ...first, disbursementDate: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Duration (months) *</label>
                  <input type="number" value={first.duration} onChange={e => setFirst({ ...first, duration: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Monthly Principal (optional)</label>
                  <input type="number" value={first.monthlyPrincipalPayment} onChange={e => setFirst({ ...first, monthlyPrincipalPayment: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Application Fee *</label>
                  <input type="number" value={first.applicationFee} onChange={e => setFirst({ ...first, applicationFee: e.target.value })} required />
                </div>
              </div>
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Saving...' : 'Save First Loan'}
                </button>
              </div>
            </form>
          )}

          {tab === 'topup' && (
            <form onSubmit={submitTopups}>
              <div className="info-box info-box-primary">
                <p>
                  <strong>Top-Up Loan:</strong> Use this for ADDITIONAL loans when the member already has an existing loan of the same type.
                </p>
                <p className="text-primary">
                  <strong>This will automatically CONSOLIDATE with existing loans:</strong>
                </p>
                <ul className="consolidation-list">
                  <li>Calculates remaining balance of existing loans at top-up date</li>
                  <li>Adds new loan amount to get total consolidated balance</li>
                  <li>Calculates new unified monthly payment based on longest remaining term</li>
                </ul>
              </div>
              
              <div className="form-grid form-grid-single">
                <div className="form-group">
                  <label>Member *</label>
                  <MemberSearch members={members} value={topMemberId} onChange={setTopMemberId} />
                </div>
              </div>

              <div className="section-divider">
                <h4>Add Top-Up Loans</h4>
              </div>

              {topups.map((x, i) => (
                <div key={i} className="topup-row">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Loan Type *</label>
                      <select value={x.loanType} onChange={e => {
                        const arr = [...topups]; arr[i] = { ...arr[i], loanType: e.target.value }; setTopups(arr);
                      }}>
                        <option value="normal">Normal</option>
                        <option value="school">School</option>
                        <option value="emergency">Emergency</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Amount *</label>
                      <input type="number" value={x.originalAmount} onChange={e => {
                        const arr = [...topups]; arr[i] = { ...arr[i], originalAmount: e.target.value }; setTopups(arr);
                      }} />
                    </div>
                    <div className="form-group">
                      <label>Disbursement Date *</label>
                      <input type="date" value={x.disbursementDate} onChange={e => {
                        const arr = [...topups]; arr[i] = { ...arr[i], disbursementDate: e.target.value }; setTopups(arr);
                      }} />
                    </div>
                    <div className="form-group">
                      <label>Duration (months) *</label>
                      <input type="number" value={x.duration} onChange={e => {
                        const arr = [...topups]; arr[i] = { ...arr[i], duration: e.target.value }; setTopups(arr);
                      }} />
                    </div>
                    <div className="form-group">
                      <label>Monthly Principal (optional)</label>
                      <input type="number" value={x.monthlyPrincipalPayment} onChange={e => {
                        const arr = [...topups]; arr[i] = { ...arr[i], monthlyPrincipalPayment: e.target.value }; setTopups(arr);
                      }} />
                    </div>
                    <div className="form-group">
                      <label>Application Fee *</label>
                      <input type="number" value={x.applicationFee} onChange={e => {
                        const arr = [...topups]; arr[i] = { ...arr[i], applicationFee: e.target.value }; setTopups(arr);
                      }} />
                    </div>
                    <div className="form-group form-group-action">
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => setTopups(prev => prev.filter((_, idx) => idx !== i))}>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="add-row-actions">
                <button type="button" className="btn btn-outline" onClick={() => setTopups(prev => ([...prev, { ...defaultTopupRow }]))}>
                  + Add another top-up
                </button>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Importing...' : 'Import Top-Up(s) with Consolidation'}
                </button>
              </div>
            </form>
          )}

          {tab === 'excel' && (
            <div>
              <div className="info-box info-box-muted">
                <p><strong>Excel format:</strong> memberId, loanType (normal/school/emergency), originalAmount, disbursementDate (any common format), duration (months), monthlyPrincipalPayment (optional), applicationFee (optional), initialShares (optional), initialSharesDate (optional), sharesStartMonth (optional, YYYY-MM), monthlySharesContribution (optional).</p>
                <p className="text-muted">
                  <strong>Date formats accepted:</strong> DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD, DD-MM-YYYY, MM-DD-YYYY, or Excel date numbers.
                </p>
                <p className="text-muted">
                  <strong>Flexible Import Options:</strong> Each row can have:
                </p>
                <ul className="text-muted" style={{marginLeft: '20px', fontSize: '0.9em'}}>
                  <li><strong>Loan only:</strong> Provide originalAmount, loanType, disbursementDate, duration (omit shares fields or set to 0)</li>
                  <li><strong>Shares only:</strong> Provide shares fields, set originalAmount to 0 or omit loan fields</li>
                  <li><strong>Both:</strong> Provide both loan and shares data</li>
                </ul>
                <p className="text-muted">
                  <strong>Note:</strong> For each member, the FIRST row becomes the initial loan, and subsequent rows become top-ups that are automatically consolidated.
                </p>
                <p className="text-muted">
                  <strong>Shares - Two Types:</strong>
                </p>
                <ul className="text-muted" style={{marginLeft: '20px', fontSize: '0.9em'}}>
                  <li><strong>Initial Shares</strong> (one-time deposit): Use initialShares + initialSharesDate</li>
                  <li><strong>Monthly Plan</strong> (recurring): Use sharesStartMonth (YYYY-MM format like 2024-01) + monthlySharesContribution. System will backfill from start month to today automatically.</li>
                </ul>
              </div>

              <div className="excel-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    // Create headers with guidance
                    const headers = [
                      'memberId', 
                      'loanType (normal/school/emergency)', 
                      'originalAmount', 
                      'disbursementDate (any date format)', 
                      'duration (months)', 
                      'monthlyPrincipalPayment (optional)', 
                      'applicationFee (optional)',
                      'initialShares (optional)',
                      'initialSharesDate (optional)',
                      'sharesStartMonth (optional, YYYY-MM)',
                      'monthlySharesContribution (optional)'
                    ];
                    
                    // Add comprehensive sample rows showing all scenarios
                    const sampleRows = [
                      // First member: normal loan with shares
                      [
                        'S00001',
                        'normal',
                        '50000',
                        '15/01/2024',
                        '12',
                        '',
                        '500',
                        '10000',
                        '01/01/2024',
                        '2024-01',
                        '1000'
                      ],
                      // Same member: top-up normal loan (no shares - only first row has shares)
                      [
                        'S00001',
                        'normal',
                        '30000',
                        '15/06/2024',
                        '8',
                        '',
                        '300',
                        '',
                        '',
                        '',
                        ''
                      ],
                      // Same member: school loan
                      [
                        'S00001',
                        'school',
                        '80000',
                        '01/02/2024',
                        '10',
                        '',
                        '800',
                        '',
                        '',
                        '',
                        ''
                      ],
                      // Different member: emergency loan with shares
                      [
                        'S00002',
                        'emergency',
                        '20000',
                        '10/03/2024',
                        '6',
                        '',
                        '200',
                        '5000',
                        '01/03/2024',
                        '2024-03',
                        '500'
                      ],
                      // Another member: shares only (no loan)
                      [
                        'S00003',
                        '',
                        '0',
                        '',
                        '',
                        '',
                        '',
                        '15000',
                        '01/01/2024',
                        '2024-01',
                        '2000'
                      ]
                    ];
                    
                    const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
                    
                    // Set column widths for better readability
                    ws['!cols'] = [
                      { wch: 12 },  // memberId
                      { wch: 30 },  // loanType with guidance
                      { wch: 15 },  // originalAmount
                      { wch: 30 },  // disbursementDate with guidance
                      { wch: 18 },  // duration with guidance
                      { wch: 30 },  // monthlyPrincipalPayment
                      { wch: 25 },  // applicationFee
                      { wch: 25 },  // initialShares
                      { wch: 25 },  // initialSharesDate
                      { wch: 35 },  // sharesStartMonth
                      { wch: 35 }   // monthlySharesContribution
                    ];
                    
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'HistoricalLoans');
                    XLSX.writeFile(wb, 'historical_loans_template.xlsx');
                  }}
                >
                  Download Excel Template
                </button>
              </div>

              <div className="form-group">
                <label>Select Excel File</label>
                <input type="file" accept=".xlsx,.xls" onChange={handleExcel} />
              </div>

              {excelRows.length > 0 && (
                <div className="excel-preview">
                  <small>Preview first 5 rows:</small>
                  <pre>{JSON.stringify(excelRows.slice(0, 5), null, 2)}</pre>
                </div>
              )}

              <div className="form-actions">
                <button className="btn btn-primary" disabled={busy || excelRows.length === 0} onClick={submitExcel}>
                  {busy ? 'Importing...' : 'Import Excel'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}